import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { whatsappChannelConnected } from "@/lib/whatsappConnection";

// Whether a business has at least one *working* messaging channel connected.
//
// Connection state is stored per-channel in different places (there is no
// single flag), so this checks both the legacy business columns and the
// canonical connection table:
//   - Telegram        → businesses.telegram_connected_at (bot or business mode)
//   - WhatsApp (Meta)  → provider='meta_cloud' + connected timestamp + phone ID
//     AND whatsapp_status='connected'. Stored credentials are not enough:
//     Meta only delivers inbound once the number is registered for Cloud API,
//     and the backend writes 'connected' only when Meta reports CONNECTED.
//     Counting a pending number as a working channel is what let a business
//     switch on workflows that could never fire.
//   - Historical WhatsApp providers remain recognised for migration continuity,
//     but all new dashboard connections use Meta Embedded Signup.
//   - Instagram, Messenger, LINE, and migrated providers
//                     → business_channel_connections.status='connected'
//     (the canonical connected value per _shared/connectors/credentials.ts)
//
// This is the gate the onboarding + dashboard use to decide "live vs sandbox":
// a business only operates live (and pays a full plan) once it has a connected
// channel AND an enabled workflow. Missing either → $1 Sandbox.

export interface ChannelConnectionState {
  hasConnectedChannel: boolean;
  connectedChannels: string[];
  loading: boolean;
}

export async function fetchConnectedChannels(businessId: string): Promise<string[]> {
  const channels: string[] = [];

  const [{ data: biz }, { data: rows }] = await Promise.all([
    supabase
      .from("businesses")
      .select(
        "telegram_connected_at, whatsapp_provider, whatsapp_status, whatsapp_number, meta_connected_at, meta_phone_number_id",
      )
      .eq("business_id", businessId)
      .maybeSingle(),
    supabase
      .from("business_channel_connections")
      .select("channel, status")
      .eq("business_id", businessId)
      .eq("status", "connected"),
  ]);

  if (biz?.telegram_connected_at) channels.push("telegram");
  if (whatsappChannelConnected(biz)) channels.push("whatsapp");
  for (const r of rows ?? []) {
    if (r.channel && !channels.includes(r.channel)) channels.push(r.channel);
  }
  return channels;
}

export function useChannelConnection(businessId: string | null | undefined): ChannelConnectionState & {
  refresh: () => Promise<void>;
} {
  const [state, setState] = useState<ChannelConnectionState>({
    hasConnectedChannel: false,
    connectedChannels: [],
    loading: true,
  });

  const refresh = useCallback(async () => {
    if (!businessId) {
      setState({ hasConnectedChannel: false, connectedChannels: [], loading: false });
      return;
    }
    try {
      const channels = await fetchConnectedChannels(businessId);
      setState({ hasConnectedChannel: channels.length > 0, connectedChannels: channels, loading: false });
    } catch (err) {
      console.error("useChannelConnection: failed to load channels", err);
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, [businessId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { ...state, refresh };
}
